import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useScrollRestoration } from './hooks/useScrollRestoration';
import { useTelegramBackButton } from './hooks/useTelegramBackButton';
import { useTelegram } from './hooks/useTelegram';
import { useUserStore, useSettingsStore, useCatalogStore, useCartStore, useFavoritesStore } from './store';
import { SplashScreen } from './components/SplashScreen';
import { BlockedScreen } from './components/BlockedScreen';
import { MaintenanceScreen } from './components/MaintenanceScreen';
import { api } from './services/api';
import {
  HomePage,
  CatalogPage,
  ProductDetailPage,
  FavoritesPage,
  CartPage,
  PaymentPage,
  PaymentReturnPage,
  OrderStatusPage,
  ProfilePage,
  OrdersHistoryPage,
  PrivacyPage,
  TermsPage,
  DonatCatalogPage,
  RegionSelectPage,
  SteamTopupPage,
  CodesPage,
  CodeServicePage,
  SharingHomePage,
  SharingCollectionPage,
  SharingProductPage,
  SharingPositionsPage,
} from './pages';

function ScrollRestorationWrapper() {
  useScrollRestoration();
  // Кнопка «назад» Telegram — на всех роутах, включая страницы без Layout
  // (иначе на посадочной оставалась стрелка от предыдущего экрана и не работала).
  useTelegramBackButton();
  return null;
}

// Регион на входе больше не спрашиваем: в каталог можно попасть и мимо карточки
// PS Store (например, «Главная» в таббаре из профиля). Тогда, как и на карточке,
// ставим Украину — сменить ее можно флажком в каталоге.
function RegionGuard({ children }: { children: React.ReactNode }) {
  const lastSelectedRegion = useUserStore((s) => s.lastSelectedRegion);
  const setLastSelectedRegion = useUserStore((s) => s.setLastSelectedRegion);
  useEffect(() => {
    if (!lastSelectedRegion) setLastSelectedRegion('UA');
  }, [lastSelectedRegion, setLastSelectedRegion]);
  if (!lastSelectedRegion) return null;
  return <>{children}</>;
}

function App() {
  const { user, isReady } = useTelegram();
  const { user: currentUser, setUser, blocked, setBlocked } = useUserStore();
  const { loadSettings, settings } = useSettingsStore();
  const preload = useCatalogStore((s) => s.preload);
  const catalogLoaded = useCatalogStore((s) => s.loaded);
  const loadCart = useCartStore((s) => s.loadCart);
  const clearCart = useCartStore((s) => s.clearCart);
  const loadFavorites = useFavoritesStore((s) => s.loadFavorites);
  const clearFavorites = useFavoritesStore((s) => s.clearFavorites);

  const [timerDone, setTimerDone] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [splashGone, setSplashGone] = useState(false);
  const readyToHide = useRef(false);

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    const getInitData = () => typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp?.initData;
    const isInTelegram = () => typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp;

    loadSettings().then(() => {
      if (user) {
        if (!currentUser || currentUser.id !== user.id.toString()) {
          clearCart();
          clearFavorites();
        }
        setUser({
          id: user.id.toString(),
          username: user.username,
          firstName: user.first_name,
          lastName: user.last_name,
        });
      }

      const runAuthCheck = () => {
        if (getInitData()) {
          api.getMe()
            .then((me) => {
              setBlocked(me.blocked);
              if (!me.blocked) {
                loadCart();
                loadFavorites();
              }
              setAuthChecked(true);
            })
            .catch((err: Error & { code?: string }) => {
              if (err.code === 'USER_BLOCKED') setBlocked(true);
              else {
                loadCart();
                loadFavorites();
              }
              setAuthChecked(true);
            });
        } else {
          setBlocked(false);
          setAuthChecked(true);
        }
      };

      if (getInitData()) {
        runAuthCheck();
      } else if (isInTelegram()) {
        const deadline = Date.now() + 2000;
        const poll = () => {
          if (getInitData()) {
            runAuthCheck();
            return;
          }
          if (Date.now() < deadline) {
            setTimeout(poll, 100);
          } else {
            setBlocked(false);
            setAuthChecked(true);
          }
        };
        setTimeout(poll, 150);
      } else {
        setBlocked(false);
        setAuthChecked(true);
      }
    });
  }, [isReady, user, currentUser?.id, setUser, setBlocked, loadSettings, loadCart, clearCart, loadFavorites, clearFavorites]);

  // Бонусный баланс тянем один раз на старте: экраны читают его из хранилища
  // и рисуют мгновенно, без запроса на каждом переходе
  const setCachedBonus = useUserStore((s) => s.setCachedBonus);
  useEffect(() => {
    if (!authChecked) return;
    api.getBalance()
      .then((b) => setCachedBonus(b.balance))
      .catch(() => {});
  }, [authChecked, setCachedBonus]);

  useEffect(() => { preload(); }, [preload]);

  // Re-fetch data when user returns to the app
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') preload();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [preload]);

  // Fade out only when BOTH timer and data are ready
  useEffect(() => {
    if (timerDone && catalogLoaded && !readyToHide.current) {
      readyToHide.current = true;
      // Let browser paint the HomePage content first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFadeOut(true);
          setTimeout(() => setSplashGone(true), 300);
        });
      });
    }
  }, [timerDone, catalogLoaded]);

  const isInTelegram = typeof window !== 'undefined' && !!(window as any).Telegram?.WebApp;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) {
      const path = window.location.pathname;
      if (path !== '/privacy' && path !== '/terms' && !path.startsWith('/payment-return')) {
        window.location.replace('https://telegram.me/XPlay_Store_bot');
      }
    }
  }, []);

  if (isInTelegram && !authChecked) {
    return <SplashScreen minDisplayTime={0} onComplete={() => {}} />;
  }
  if (settings.maintenance?.enabled) {
    const currentUsername = (user?.username || '').replace(/^@/, '').toLowerCase();
    const allowed = (settings.maintenance.allowedUsernames || []).map((u) => u.replace(/^@/, '').toLowerCase());
    const bypass = currentUsername && allowed.includes(currentUsername);
    if (!bypass) {
      return <MaintenanceScreen />;
    }
  }
  if (blocked) {
    return <BlockedScreen />;
  }

  return (
    <BrowserRouter>
      <ScrollRestorationWrapper />
      {!splashGone && (
        <div
          className={`fixed inset-0 z-[200] transition-opacity duration-300 ease-out ${
            fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        >
          <SplashScreen
            minDisplayTime={800}
            onComplete={() => setTimerDone(true)}
          />
        </div>
      )}

      <Routes>
        <Route path="/" element={<Navigate to="/main" replace />} />
        <Route path="/entry" element={<Navigate to="/main" replace />} />
        {/* Первый экран: PS Store, Steam, коды пополнения, Telegram-канал */}
        <Route path="/main" element={<RegionSelectPage />} />

        <Route path="/region-select" element={<RegionSelectPage />} />
        <Route path="/region-change" element={<RegionSelectPage editMode />} />
        <Route path="/home" element={<RegionGuard><HomePage /></RegionGuard>} />
        <Route path="/catalog" element={<RegionGuard><CatalogPage /></RegionGuard>} />
        <Route path="/catalog/:collectionId" element={<RegionGuard><CatalogPage /></RegionGuard>} />
        <Route path="/product/:id" element={<RegionGuard><ProductDetailPage /></RegionGuard>} />
        <Route path="/donats/:gameName" element={<RegionGuard><DonatCatalogPage /></RegionGuard>} />
        <Route path="/favorites" element={<RegionGuard><FavoritesPage /></RegionGuard>} />
        <Route path="/cart" element={<RegionGuard><CartPage /></RegionGuard>} />
        {/* Профиль и коды доступны с посадочной до выбора региона PS */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/orders" element={<RegionGuard><OrdersHistoryPage /></RegionGuard>} />
        {/* Кешбэк и рефералка переехали в профиль — старые ссылки ведут туда же */}
        <Route path="/balance" element={<Navigate to="/profile" replace />} />
        <Route path="/codes" element={<CodesPage />} />
        <Route path="/codes/:serviceKey" element={<CodeServicePage />} />
        {/* Steam-топап не зависит от региона PS — доступен с посадочной до выбора региона */}
        <Route path="/steam-topup" element={<SteamTopupPage />} />
        {/* Шеринг: каталог без региона PS — доступен с главного экрана */}
        <Route path="/sharing" element={<SharingHomePage />} />
        <Route path="/sharing/collection/:id" element={<SharingCollectionPage />} />
        <Route path="/sharing/positions" element={<SharingPositionsPage />} />
        <Route path="/sharing/:id" element={<SharingProductPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route path="/payment/:orderId" element={<PaymentPage />} />
        <Route path="/payment-return" element={<PaymentReturnPage />} />
        <Route path="/order-status/:orderId" element={<OrderStatusPage />} />

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
